/**
 * Модель настраиваемого списка (ТЗ §5.1).
 *
 * Определение представления здесь и `saved_view.definition` на сервере — одна и та же
 * структура намеренно: сохранённое представление это запрос без курсора, и две формы
 * разошлись бы до того, что сохранённый вид перестал бы воспроизводиться.
 */

/** Операторы фильтра. Перечисление, а не строка: список закрыт и на сервере тоже. */
export type FilterOperator =
  | 'eq'
  | 'ne'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'contains'
  | 'starts_with'
  | 'between'
  | 'in'
  | 'is_null'
  | 'is_not_null'

/** Тип колонки — от него зависит, какие операторы предлагать. */
export type ColumnType = 'text' | 'number' | 'date' | 'timestamp' | 'uuid' | 'boolean'

export interface ColumnDescriptor {
  name: string
  type: ColumnType
  sortable: boolean
  filterable: boolean
  /** Подпись пользовательского поля (#106); у штатных колонок нет — их подписывает словарь. */
  label?: string | null
  /**
   * Колонка-ссылка: строка страницы несёт рядом `<name>__label` — название записи, на
   * которую она указывает. `entity` — список, на карточку которого вести; без него у цели
   * своего списка нет (стадия, склад), и название показывается без перехода.
   */
  reference?: { entity?: string | null } | null
  /**
   * Денежная колонка (#91): тип у суммы и у количества один (`number`), а показ разный.
   * `currencyKey` — ключ строки с кодом валюты (`<name>__currency`); без него у строки
   * валюты нет, и сумма печатается без знака, а не рублями.
   */
  money?: { currencyKey?: string | null } | null
}

/** Описание списка: что можно показать, отфильтровать, импортировать. */
export interface EntityDescriptor {
  entity: string
  resource: string
  columns: ColumnDescriptor[]
  defaultColumns: string[]
  importable: boolean
  importColumns: string[]
  importRequired: string[]
}

export interface FilterSpec {
  column: string
  operator: FilterOperator
  value?: unknown
}

export interface SortSpec {
  column: string
  descending: boolean
}

/** То, что лежит в `saved_view.definition` и уходит телом запроса страницы. */
export interface ListViewDefinition {
  columns: string[]
  filters: FilterSpec[]
  sort: SortSpec[]
  groupBy?: string | null
  pageSize?: number
}

export interface ListRow {
  id: string
  [column: string]: unknown
}

/**
 * Страница списка.
 *
 * Признак последней страницы — `hasMore`, а не общее число строк: `count(*)` стоил бы
 * полного скана на каждом запросе, то есть ровно той цены, ради избавления от которой
 * пагинация сделана курсорной.
 */
export interface ListPage {
  rows: ListRow[]
  columns: string[]
  nextCursor: string | null
  hasMore: boolean
}

export interface ListGroup {
  value: unknown
  count: number
}

export interface SavedView {
  id: string
  entityType: string
  name: string
  /** JSON-текст: форму документа определяет интерфейс, а не сервер. */
  definition: string
  shared: boolean
  defaultView: boolean
}

/** Ошибка одной строки импорта. */
export interface ImportRowError {
  /** Номер строки как в Excel: заголовок — строка 1. */
  line: number
  column: string | null
  message: string
}

/**
 * Отчёт импорта.
 *
 * Успех и провал не сводятся к флагу: файл, из которого встало 1900 строк из 2000, — не
 * то и не другое, и одна цифра потеряла бы ровно ту половину, с которой надо работать.
 */
export interface ImportReport {
  totalRows: number
  imported: number
  failed: number
  errors: ImportRowError[]
}

export interface BulkResult {
  requested: number
  /** Сколько строк изменилось на самом деле — может быть меньше выбранного. */
  affected: number
}

/** Пустое определение: список открывается колонками по умолчанию. */
export function emptyDefinition(): ListViewDefinition {
  return { columns: [], filters: [], sort: [] }
}

/**
 * Операторы, применимые к колонке.
 *
 * У колонки-ссылки (`reference`) текстовые операторы ищут по названию записи, на которую она
 * указывает (#89): кладовщик вводит «болт», а не UUID товара. Равенство по ключу ей не
 * предлагается — ввести идентификатор руками никто не может, и пункт вёл бы к отказу.
 */
export function operatorsFor(type: ColumnType, reference?: boolean): FilterOperator[] {
  if (reference) return ['contains', 'starts_with', 'is_null', 'is_not_null']
  const common: FilterOperator[] = ['eq', 'ne', 'is_null', 'is_not_null']
  switch (type) {
    case 'text':
      return ['contains', 'starts_with', ...common, 'in']
    case 'number':
    case 'date':
    case 'timestamp':
      return [...common, 'gt', 'gte', 'lt', 'lte', 'between']
    case 'boolean':
      return ['eq']
    case 'uuid':
      return ['eq', 'ne', 'in', 'is_null', 'is_not_null']
  }
}

/** Нужно ли оператору значение. */
export function takesValue(operator: FilterOperator): boolean {
  return operator !== 'is_null' && operator !== 'is_not_null'
}
